/* ============================================================
   Lunelle — static site runtime
   One file: shared chrome (nav/footer), every PDP interaction from the
   theme's lunelle.js, and a real localStorage cart + checkout.
   ============================================================ */
(() => {
  'use strict';

  /* ========================================================
     1. CATALOG
     Single source of truth for prices. Everything else on the site
     (labels, sticky bar, cart, checkout) is derived from this table.
     ======================================================== */
  const CURRENCY = '$';
  const PRODUCT = {
    title: 'Heatless Curling Kit',
    meta: 'Pastel pink · satin',
    url: '/products/lunelle-heatless-curling-kit',
    image: (window.LUNELLE_ASSETS + 'flatlay-satin.jpg'),
  };
  const VARIANTS = {
    'pack-1': { id: 'pack-1', label: '1 kit',  kits: 1, price: 29.95, compare:  29.95 },
    'pack-2': { id: 'pack-2', label: '2 kits', kits: 2, price: 52.45, compare:  59.90 },
    'pack-3': { id: 'pack-3', label: '3 kits', kits: 3, price: 65.85, compare:  89.85 },
  };
  /* Stock shown on the product page. Set `left` to a number you will
     actually honour: a batch you have reserved with the supplier, or a
     launch run you close when it sells out. A figure you never enforce
     is a claim you cannot back. Set `left` to null to hide the bar. */
  const STOCK = { total: 200, left: 23 };

  const PROTECTION = { id: 'shipping-protection', label: 'Shipping Protection', price: 4.95 };
  /* Lo que cuesta de verdad un gorro de satén en Australia. Tasarlo en 19,95
     al lado de un kit de 49,95 sugería que el kit vale mucho menos. */
  const BASE_UNIT = VARIANTS['pack-1'].price;

  /* Curler colours. The bonnet in every kit matches the ribbon. */
  const COLOURS = [
    { id: 'blush',    label: 'Blush Pink', image: (window.LUNELLE_ASSETS + 'swatch-blush.jpg') },
    { id: 'burgundy', label: 'Burgundy',   image: (window.LUNELLE_ASSETS + 'swatch-burgundy.jpg') },
    { id: 'black',    label: 'Black',      image: (window.LUNELLE_ASSETS + 'swatch-black.jpg') },
  ];
  const DEFAULT_COLOUR = COLOURS[0].id;
  const colourById = (id) => COLOURS.find((c) => c.id === id) || COLOURS[0];
  const colourRank = (id) => COLOURS.findIndex((c) => c.id === id);

  /* Normalises a colour list to exactly `kits` valid entries. */
  function normaliseColours(list, kits) {
    const out = [];
    for (let i = 0; i < kits; i++) {
      const wanted = Array.isArray(list) ? list[i] : null;
      out.push(COLOURS.some((c) => c.id === wanted) ? wanted : DEFAULT_COLOUR);
    }
    return out;
  }

  /* Cart lines are keyed by variant + colour combination, so "2 kits in
     blush" and "2 kits in black" stay as two separate lines. */
  const lineKey = (item) => item.id + '|' + (item.colours || []).join(',');

  const money = (v) => CURRENCY + (Math.round((Number(v) || 0) * 100) / 100).toFixed(2);

  /* ========================================================
     2. CART STORE (localStorage)
     ======================================================== */
  const CART_KEY = 'lunelle_cart_v1';
  const EMPTY = { items: [], protection: false };

  function readCart() {
    try {
      const raw = JSON.parse(localStorage.getItem(CART_KEY) || 'null');
      if (!raw || !Array.isArray(raw.items)) return { ...EMPTY };
      const items = raw.items
        .filter((i) => i && VARIANTS[i.id] && Number(i.qty) > 0)
        .map((i) => ({
          id: i.id,
          qty: Math.min(99, Math.max(1, parseInt(i.qty, 10) || 1)),
          /* Carts saved before colours existed fall back to the default. */
          colours: normaliseColours(i.colours, VARIANTS[i.id].kits),
        }));
      return { items, protection: !!raw.protection };
    } catch { return { ...EMPTY }; }
  }

  function writeCart(cart) {
    try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch {}
    document.dispatchEvent(new CustomEvent('lunelle:cart', { detail: cart }));
    return cart;
  }

  const Cart = {
    get: readCart,
    add(variantId, qty = 1, colours = null) {
      const cart = readCart();
      if (!VARIANTS[variantId]) return cart;
      const picked = normaliseColours(colours, VARIANTS[variantId].kits);
      const candidate = { id: variantId, qty, colours: picked };
      const line = cart.items.find((i) => lineKey(i) === lineKey(candidate));
      if (line) line.qty = Math.min(99, line.qty + qty);
      else cart.items.push(candidate);
      return writeCart(cart);
    },
    setQty(key, qty) {
      const cart = readCart();
      const idx = cart.items.findIndex((i) => lineKey(i) === key);
      if (idx < 0) return cart;
      if (qty <= 0) cart.items.splice(idx, 1);
      else cart.items[idx].qty = Math.min(99, qty);
      if (!cart.items.length) cart.protection = false;
      return writeCart(cart);
    },
    remove(key) { return Cart.setQty(key, 0); },
    setProtection(on) {
      const cart = readCart();
      cart.protection = !!on && cart.items.length > 0;
      return writeCart(cart);
    },
    clear() { return writeCart({ ...EMPTY, items: [] }); },
    /* Cuenta kits, no líneas: dos packs de 2 son cuatro kits, y eso es lo que
       espera ver quien mira la burbuja del carrito. */
    count(cart = readCart()) {
      return cart.items.reduce((n, i) => n + VARIANTS[i.id].kits * i.qty, 0) + (cart.protection ? 1 : 0);
    },
    totals(cart = readCart()) {
      let items = 0, compare = 0, kits = 0;
      cart.items.forEach((i) => {
        const v = VARIANTS[i.id];
        items += v.price * i.qty;
        compare += v.compare * i.qty;
        kits += v.kits * i.qty;
      });
      const protection = cart.protection && cart.items.length ? PROTECTION.price : 0;
      const itemSavings = Math.max(0, compare - items);
      return {
        items, compare, kits, protection,
        itemSavings,
        /* Solo el descuento por pack. Antes sumaba el envio y un gorro por kit
           a precio declarado, y el ahorro salia mayor que el propio pedido. Ni
           el envio ni el gorro se han cobrado nunca, asi que no se ahorran. */
        savings: itemSavings,
        subtotal: items + protection,
        empty: cart.items.length === 0,
      };
    },
  };
  window.LunelleCart = Cart;

  /* ========================================================
     2b. CHECKOUT HAND-OFF
     This cart lives in localStorage; Shopify keeps its own, and nothing is
     charged until the two agree. So the checkout control stops behaving like
     a link: it empties Shopify's cart, replays ours into it line by line, and
     only then leaves the site.

     Colour is a real product option, so every pack+colour combination is its
     own Shopify variant. It used to travel as a line property instead, which
     read fine on the order but was invisible to everything downstream — DSers
     maps variant to variant, so it would have ordered the same colour from the
     supplier whatever the shopper chose. This table is generated by
     sync-variant-map.js from the live product; do not edit it by hand.
     ======================================================== */
  const SHOPIFY_VARIANTS = {
    'pack-1|blush':                     59186273648985, // 1 kit / Blush Pink
    'pack-1|burgundy':                  59187226771801, // 1 kit / Burgundy
    'pack-1|black':                     59187226804569, // 1 kit / Black
    'pack-2|blush,blush':               59186277450073, // 2 kits / Blush Pink x2
    'pack-2|blush,burgundy':            59187226837337, // 2 kits / Blush Pink + Burgundy
    'pack-2|blush,black':               59187226870105, // 2 kits / Blush Pink + Black
    'pack-2|burgundy,burgundy':         59187226902873, // 2 kits / Burgundy x2
    'pack-2|burgundy,black':            59187226935641, // 2 kits / Burgundy + Black
    'pack-2|black,black':               59187226968409, // 2 kits / Black x2
    'pack-3|blush,blush,blush':         59186277482841, // 3 kits / Blush Pink x3
    'pack-3|blush,blush,burgundy':      59187227001177, // 3 kits / Blush Pink x2 + Burgundy
    'pack-3|blush,blush,black':         59187227033945, // 3 kits / Blush Pink x2 + Black
    'pack-3|blush,burgundy,burgundy':   59187227066713, // 3 kits / Blush Pink + Burgundy x2
    'pack-3|blush,burgundy,black':      59187227099481, // 3 kits / Blush Pink + Burgundy + Black
    'pack-3|blush,black,black':         59187227132249, // 3 kits / Blush Pink + Black x2
    'pack-3|burgundy,burgundy,burgundy':59187227165017, // 3 kits / Burgundy x3
    'pack-3|burgundy,burgundy,black':   59187227197785, // 3 kits / Burgundy x2 + Black
    'pack-3|burgundy,black,black':      59187227230553, // 3 kits / Burgundy + Black x2
    'pack-3|black,black,black':         59187227263321, // 3 kits / Black x3
  };
  const SHOPIFY_PROTECTION = 59186564989273;

  /* Reads the body as text first: when the storefront is behind its password,
     or the route is wrong, Shopify answers with an HTML page and calling
     .json() straight away throws a parse error that hides the real cause. */
  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then((r) => r.text().then((text) => {
      let data = null;
      try { data = JSON.parse(text); } catch (_) {}
      if (r.ok && data) return data;
      const detail = data
        ? (data.description || data.message || JSON.stringify(data).slice(0, 160))
        : (/^\s*</.test(text) ? 'the server answered with a web page, not JSON' : text.slice(0, 160));
      throw new Error(url + ' → HTTP ' + r.status + ': ' + detail);
    }));
  }

  /* The cart keeps colours in the order they were picked; Shopify holds one
     variant per combination, and two blush plus one black is the same box
     whichever order it was chosen in. Sorting into the canonical colour order
     is what makes the two sides agree. */
  const variantKey = (line) =>
    line.id + '|' + line.colours.slice().sort((a, b) => colourRank(a) - colourRank(b)).join(',');

  /* Lo que el carrito de Shopify tiene que contener, por variante. */
  function lineasQueridas(cart) {
    const querido = {};
    for (const line of cart.items) {
      const key = variantKey(line);
      const id = SHOPIFY_VARIANTS[key];
      if (!id) throw new Error('No Shopify variant mapped for ' + key);
      querido[id] = (querido[id] || 0) + line.qty;
    }
    if (cart.protection) querido[SHOPIFY_PROTECTION] = 1;
    return querido;
  }

  async function handOffToShopify() {
    const cart = Cart.get();
    if (!cart.items.length) return false;
    const querido = lineasQueridas(cart);

    /* Antes esto hacia clear + un add por linea. Cada /cart/add.js dispara
       "product_added_to_cart", y desde que el boton de anadir avisa a Shopify
       por su cuenta eso contaba el AddToCart DOS veces por persona: una al
       anadir y otra aqui. /cart/update.js fija las cantidades de una sola vez
       y no dispara ese evento, asi que el carrito queda igual y Meta cuenta una. */
    try {
      const actual = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) => r.json());
      const updates = Object.assign({}, querido);
      (actual.items || []).forEach((it) => {
        if (!(it.variant_id in updates)) updates[it.variant_id] = 0;
      });
      await postJson('/cart/update.js', { updates });
      return true;
    } catch (err) {
      /* Plan B: el camino de siempre. Duplica el evento, pero poder cobrar
         importa mas que contar bien. */
      console.warn('[lunelle] /cart/update.js fallo, reconstruyo el carrito:', err);
      await postJson('/cart/clear.js', {});
      for (const line of cart.items) {
        const key = variantKey(line);
        const id = SHOPIFY_VARIANTS[key];
        if (!id) throw new Error('No Shopify variant mapped for ' + key);
        await postJson('/cart/add.js', { id, quantity: line.qty });
      }
      if (cart.protection) await postJson('/cart/add.js', { id: SHOPIFY_PROTECTION, quantity: 1 });
      return true;
    }
  }
  window.lunelleCheckout = handOffToShopify;

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.cart-checkout, [data-checkout]');
    if (!btn) return;
    /* El preventDefault va ANTES del guardia: si sale por el return de abajo
       sin haberlo llamado, el navegador sigue el enlace por su cuenta y se
       planta en /checkout con el carrito de Shopify a medio montar. */
    e.preventDefault();
    if (btn.dataset.busy) return;
    btn.dataset.busy = '1';
    const label = btn.textContent;
    btn.textContent = 'Taking you to checkout…';
    const release = () => { btn.textContent = label; delete btn.dataset.busy; };
    handOffToShopify()
      .then((ok) => { if (ok) window.location.href = '/checkout'; else release(); })
      .catch((err) => {
        release();
        /* The reason is worth surfacing: a silent "try again" sends the reader
           round the same loop and tells whoever has to fix it nothing. */
        console.error('[lunelle] checkout hand-off failed:', err);
        alert('We could not pass your cart to checkout.\n\n' + err.message);
      });
  });

  /* ========================================================
     3. SHARED CHROME (nav + mobile menu + footer)
     ======================================================== */
  const LOGO = '<svg class="logo-mark" viewBox="-6 0 132 132" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Lunelle">' +
    '<g stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" fill="none">' +
    '<path d="M31 95V16.5a6 6 0 0 1 12 0V92" stroke-width="6"/>' +
    '<path d="M33 100c-9-5-20 0-20 9s12 13 19 6c6-6 15-7 25-8 14-1 28 6 40-2 12-8 8-23-7-26s-26 5-24 16" stroke-width="8.5"/>' +
    '</g></svg>';

  function buildChrome() {
    const page = document.body.dataset.page || '';
    const blush = document.body.hasAttribute('data-blush');
    const active = (n) => (page === n ? ' class="active"' : '');
    const header = document.querySelector('[data-site-header]');
    if (header) {
      header.outerHTML =
        '<div class="safe-area-cover ' + (blush ? 'safe-area-cover-dark' : 'safe-area-cover-light') + '" aria-hidden="true"></div>' +
        '<header class="nav' + (blush ? ' on-blush' : '') + '" aria-label="Main navigation"><div class="nav-inner">' +
          /* One-product shop: the wordmark goes to the product, not a landing page. */
          '<a href="/products/lunelle-heatless-curling-kit" class="wordmark" aria-label="Lunelle — Heatless Curling Kit"><span class="wordmark-mark">' + LOGO + '</span>' +
          '<span class="wordmark-text">Lunelle<span class="dot">.</span></span></a>' +
          '<nav><ul class="nav-links">' +
            '<li><a href="/products/lunelle-heatless-curling-kit"' + active('product') + '>Product</a></li>' +
            '<li><a href="/products/lunelle-heatless-curling-kit#reviews-full">Reviews</a></li>' +
            '<li><a href="/products/lunelle-heatless-curling-kit#faq">FAQ</a></li>' +
          '</ul></nav>' +
          '<div class="nav-right">' +
            '<a href="/cart" class="icon-btn" aria-label="Cart">' +
              '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 3h2l2.4 12.1a2 2 0 0 0 2 1.6h8.7a2 2 0 0 0 2-1.5L22 7H6"/><circle cx="9" cy="20" r="1.4"/><circle cx="18" cy="20" r="1.4"/></svg>' +
              '<span class="badge cart-badge">0</span></a>' +
            '<a href="/products/lunelle-heatless-curling-kit#buybox" class="btn btn-primary btn-sm btn-comprar">Shop now</a>' +
            /* Mobile-only CTA. On the product page it adds the selected bundle
               straight from the top bar; anywhere else it points at the product.
               Text-only on purpose: the shared handler swaps textContent. */
            (page === 'product'
              ? '<button type="button" class="btn btn-primary btn-sm nav-atc" data-add-to-cart aria-label="Add to cart">Add</button>'
              : '<a href="/products/lunelle-heatless-curling-kit#buybox" class="btn btn-primary btn-sm nav-atc">Shop</a>') +
            '<button class="hamburger" aria-label="Menu"><span></span><span></span><span></span></button>' +
          '</div>' +
        '</div></header>' +
        '<div class="mobile-menu" aria-hidden="true">' +
          '<a href="/products/lunelle-heatless-curling-kit">Product</a>' +
          '<a href="/products/lunelle-heatless-curling-kit#reviews-full">Reviews</a><a href="/products/lunelle-heatless-curling-kit#faq">FAQ</a>' +
          '<a href="/cart">Cart</a>' +
          /* Sin importe: el buybox abre con el pack de 2 a 74,95 y este decía
             49,95, que es el único precio de la web que no se sincroniza. */
          '<a href="/products/lunelle-heatless-curling-kit#buybox" class="btn btn-primary">Shop now</a>' +
        '</div>';
    }

    const footer = document.querySelector('[data-site-footer]');
    if (footer) {
      const col = (title, links) =>
        '<div class="footer-col"><h5>' + title + '</h5><ul>' +
        links.map((l) => '<li><a href="' + l[1] + '">' + l[0] + '</a></li>').join('') + '</ul></div>';
      footer.outerHTML =
        '<footer class="footer"><div class="container"><div class="footer-grid">' +
          '<div class="footer-brand">' +
            '<a href="/products/lunelle-heatless-curling-kit" class="wordmark" aria-label="Lunelle — Heatless Curling Kit"><span class="wordmark-mark">' + LOGO + '</span>' +
            '<span class="wordmark-text">Lunelle<span class="dot">.</span></span></a>' +
            '<p>Heat-free beauty rituals. Get news and exclusive offers straight to your inbox.</p>' +
            '<form class="newsletter" data-newsletter><input type="email" placeholder="you@email.com" aria-label="Email" required>' +
            '<button type="submit">Subscribe</button></form>' +
          '</div>' +
          col('Product', [['Heatless Curling Kit', '/products/lunelle-heatless-curling-kit'], ['Specifications', '/products/lunelle-heatless-curling-kit#specs'],
                          ['Reviews', '/products/lunelle-heatless-curling-kit#reviews-full'], ['Comparison', '/products/lunelle-heatless-curling-kit#compare']]) +
          col('Company', [['About us', '/pages/about-us'], ['Sustainability', '/pages/sustainability'],
                          ['Press', '/pages/press'], ['Contact', '/pages/contact']]) +
          col('Support', [['Shipping', '/pages/shipping'], ['Returns', '/pages/returns'], ['FAQ', '/products/lunelle-heatless-curling-kit#faq']]) +
          col('Legal', [['Privacy', '/pages/privacy-policy'], ['Cookies', '/pages/cookies'],
                        ['Terms', '/pages/terms'], ['Legal notice', '/pages/legal-notice']]) +
        '</div><div class="footer-bottom">' +
          '<span>© ' + new Date().getFullYear() + ' Lunelle · All rights reserved</span>' +
          '<div class="socials">' +
            '<a href="#" aria-label="Instagram"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="4"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.6" fill="currentColor"/></svg></a>' +
            '<a href="#" aria-label="TikTok"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 3v3a4 4 0 0 0 4 4v3a7 7 0 0 1-4-1.3V16a5 5 0 1 1-5-5v3a2 2 0 1 0 2 2V3z"/></svg></a>' +
            '<a href="#" aria-label="Pinterest"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M9 21c1-4 1.5-6.5 2-9"/><path d="M11 12a3 3 0 1 1 3 3c-1 0-2-.5-2.5-1.5"/></svg></a>' +
          '</div>' +
        '</div></div></footer>';
    }
  }

  buildChrome();

  /* ========================================================
     4. CHROME BEHAVIOUR
     ======================================================== */
  const nav = document.querySelector('.nav');
  if (nav) {
    const onScroll = () => nav.classList.toggle('solid', window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  const hamb = document.querySelector('.hamburger');
  if (hamb) {
    hamb.addEventListener('click', () => document.body.classList.toggle('menu-open'));
    document.querySelectorAll('.mobile-menu a').forEach((a) =>
      a.addEventListener('click', () => document.body.classList.remove('menu-open')));
  }

  /* ========================================================
     4b. CAPTURA DE CORREO
     El descuento ya no se regala en la ficha: se cambia por un correo, que es
     lo unico que queda cuando la visita se marcha sin comprar.
     El modal lo pinta snippets/email-capture.liquid, no este fichero: Shopify
     exige un token de reCAPTCHA que fetch() no puede generar, asi que el alta
     tiene que ir por un {% form 'customer' %} de verdad.
     ======================================================== */
  const SUB_KEY = 'lunelle_sub_v1';
  const leerSub = () => { try { return JSON.parse(localStorage.getItem(SUB_KEY) || '{}'); } catch (e) { return {}; } };
  const guardarSub = (v) => { try { localStorage.setItem(SUB_KEY, JSON.stringify(v)); } catch (e) {} };

  /* Con el correo ya dado, el bloque de la ficha pasa a enseñar el codigo. */
  function abrirPromo() {
    const modal = document.querySelector('[data-ec-modal]');
    const dentro = modal ? modal.querySelector('[data-promo-code]') : null;
    const codigo = dentro ? dentro.textContent.trim() : 'LUNELLE10';
    document.querySelectorAll('[data-promo][data-promo-locked]').forEach(function (caja) {
      caja.removeAttribute('data-promo-locked');
      caja.innerHTML =
        '<span class="promo-code-text">Use code <b data-promo-code>' + codigo + '</b> at checkout for 10% off</span>' +
        '<button type="button" class="promo-code-btn" data-promo-copy aria-label="Copy discount code ' + codigo + '">Copy</button>';
    });
  }

  (function () {
    const modal = document.querySelector('[data-ec-modal]');
    if (!modal) return;
    /* Si el panel ya trae el codigo, es que Shopify acaba de aceptar el alta
       y nos ha devuelto aqui con ?customer_posted=true. */
    const recienSuscrito = !!modal.querySelector('[data-promo-code]');
    if (recienSuscrito) guardarSub({ suscrito: true });
    if (recienSuscrito || leerSub().suscrito) abrirPromo();

    let abierto = false;
    function abrir() {
      if (abierto || document.body.classList.contains('drawer-open')) return;
      abierto = true;
      modal.classList.add('open');
      document.body.classList.add('ec-open');
      const campo = modal.querySelector('#ec-email');
      if (campo) setTimeout(function () { campo.focus(); }, 120);
    }
    function cerrar(recordar) {
      abierto = false;
      modal.classList.remove('open');
      document.body.classList.remove('ec-open');
      if (recordar) { const s = leerSub(); s.cerrado = true; guardarSub(s); }
    }
    window.LunelleEmail = { open: abrir, close: cerrar };

    const s = leerSub();
    if (recienSuscrito) {
      /* Ensenar el codigo nada mas volver: es lo que la persona vino a buscar. */
      abrir();
    } else if (!s.suscrito && !s.cerrado && (document.body.getAttribute('data-page') || '') !== 'cart') {
      /* Seis segundos. Antes de eso el modal se come la primera impresion, que
         es lo unico que tenemos para convencer a quien viene de un anuncio. */
      setTimeout(abrir, 6000);
    }

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-ec-close]')) { e.preventDefault(); cerrar(true); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('open')) cerrar(true);
    });
    /* El boton "Get the code" de la ficha abre el mismo modal. */
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-promo-unlock]')) { e.preventDefault(); abrir(); }
    });

    /* El formulario del pie no puede enviar por su cuenta (mismo problema del
       CAPTCHA), asi que pasa el correo al modal y deja que lo envie el de verdad.
       Antes vaciaba el campo y ponia "Subscribed" sin mandar nada a ninguna parte. */
    document.querySelectorAll('[data-newsletter]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        const campo = form.querySelector('input');
        const destino = modal.querySelector('#ec-email');
        if (destino && campo) destino.value = (campo.value || '').trim();
        abrir();
      });
    });
  })();

  document.querySelectorAll('.footer-col h5').forEach((h) => {
    h.addEventListener('click', () => {
      if (window.innerWidth > 960) return;
      h.parentElement.classList.toggle('open');
    });
  });

  /* ========================================================
     5. BADGE + TOAST
     ======================================================== */
  function syncBadge() {
    const n = Cart.count();
    document.querySelectorAll('.cart-badge').forEach((b) => {
      b.textContent = String(n);
      b.classList.toggle('is-empty', n === 0);
    });
  }
  document.addEventListener('lunelle:cart', syncBadge);
  syncBadge();

  /* ========================================================
     6. GENERIC PAGE INTERACTIONS
     ======================================================== */
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
  document.querySelectorAll('.reveal').forEach((el) => io.observe(el));

  document.querySelectorAll('.faq-item').forEach((item) => {
    const q = item.querySelector('.faq-q');
    const a = item.querySelector('.faq-a');
    if (!q || !a) return;
    q.setAttribute('aria-expanded', 'false');
    q.addEventListener('click', () => {
      const open = item.classList.toggle('open');
      q.setAttribute('aria-expanded', open ? 'true' : 'false');
      a.style.maxHeight = open ? a.scrollHeight + 'px' : '0';
    });
  });

  const tabBtns = document.querySelectorAll('.tab-btn');
  const tabPanels = document.querySelectorAll('.tab-panel');
  tabBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      tabBtns.forEach((b) => b.classList.toggle('active', b === btn));
      tabPanels.forEach((p) => p.classList.toggle('active', p.id === 'tab-' + btn.dataset.tab));
    });
  });

  /* ---- Gallery ---- */
  (() => {
    const frame = document.querySelector('.gallery-main');
    const mainImg = frame && frame.querySelector('img');
    if (!mainImg) return;
    document.querySelectorAll('.gallery-thumb').forEach((t) => {
      t.addEventListener('click', () => {
        document.querySelectorAll('.gallery-thumb').forEach((x) => x.classList.remove('active'));
        t.classList.add('active');
        const full = t.dataset.full || (t.querySelector('img') && t.querySelector('img').src);
        if (!full) return;
        mainImg.style.opacity = '0';
        setTimeout(() => {
          mainImg.src = full;
          mainImg.alt = t.dataset.alt || mainImg.alt;
          frame.classList.toggle('is-photo', t.dataset.fit !== 'contain');
          mainImg.style.opacity = '1';
        }, 180);
      });
    });

    /* Swiping the big image moves through the gallery. It hands the gesture
       back to the page the moment it looks vertical, so scrolling past the
       photo still works; touch-action: pan-y in the CSS says the same thing
       to the browser. Each swipe just clicks a thumb, so it goes through the
       same code path as tapping one. */
    const thumbs = [...document.querySelectorAll('.gallery-thumb')];
    if (thumbs.length > 1) {
      const step = (dir) => {
        const at = Math.max(0, thumbs.findIndex((t) => t.classList.contains('active')));
        thumbs[(at + dir + thumbs.length) % thumbs.length].click();
      };
      const SWIPE = 45;
      let x0 = null, y0 = null;
      frame.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        x0 = e.clientX; y0 = e.clientY;
      });
      frame.addEventListener('pointermove', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0, dy = e.clientY - y0;
        if (Math.abs(dy) > Math.abs(dx)) { x0 = null; return; }
        if (Math.abs(dx) < SWIPE) return;
        x0 = null;
        step(dx < 0 ? 1 : -1);
      });
      const drop = () => { x0 = null; };
      ['pointerup', 'pointercancel', 'pointerleave'].forEach((ev) => frame.addEventListener(ev, drop));
      /* Otherwise a desktop drag starts the browser's own image drag */
      frame.addEventListener('dragstart', (e) => e.preventDefault());
    }
  })();

  /* ---- Stock bar ---- */
  (() => {
    const bar = document.querySelector('[data-stock-bar]');
    if (!bar || STOCK.left == null) return;
    const left = Math.max(0, Math.min(STOCK.total, STOCK.left));
    const pct = Math.max(4, Math.round((left / STOCK.total) * 100));
    /* Se busca cada pieza por separado: si alguna desaparece del marcado, se
       salta esa línea en vez de tumbar el resto del fichero. */
    const pon = (sel, fn) => { const e = bar.querySelector(sel); if (e) fn(e); };
    pon('[data-stock-left]', (e) => { e.textContent = String(left); });
    pon('[data-stock-total]', (e) => { e.textContent = String(STOCK.total); });
    pon('[data-stock-fill]', (e) => { e.style.width = pct + '%'; });
    pon('.stock-bar-track', (e) => { e.setAttribute('aria-label', left + ' of ' + STOCK.total + ' kits left'); });
    /* Under a fifth of the batch the bar turns urgent on its own. */
    bar.classList.toggle('is-low', left <= STOCK.total * 0.2);
    bar.hidden = false;
  })();

  /* ---- Before / after ---- */
  document.querySelectorAll('[data-ba]').forEach((ba) => {
    const frame = ba.querySelector('[data-ba-frame]');
    const handle = ba.querySelector('[data-ba-handle]');
    if (!frame || !handle) return;
    /* Arranca a la derecha del centro: se ve mas del ''antes'', que es lo
       que da sentido a arrastrar, y el ''despues'' queda como recompensa. */
    let pos = 62, dragging = false;
    const apply = () => {
      frame.style.setProperty('--pos', pos + '%');
      handle.setAttribute('aria-valuenow', String(Math.round(pos)));
      ba.classList.toggle('hide-before-label', pos < 24);
      ba.classList.toggle('hide-after-label', pos > 76);
    };
    const setFromX = (clientX) => {
      const r = frame.getBoundingClientRect();
      if (!r.width) return;
      pos = Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100));
      apply();
    };
    frame.addEventListener('pointerdown', (e) => {
      dragging = true; ba.classList.add('is-dragging');
      if (frame.setPointerCapture) { try { frame.setPointerCapture(e.pointerId); } catch {} }
      setFromX(e.clientX); handle.focus({ preventScroll: true });
    });
    frame.addEventListener('pointermove', (e) => { if (dragging) { e.preventDefault(); setFromX(e.clientX); } });
    const endDrag = () => { if (dragging) { dragging = false; ba.classList.remove('is-dragging'); } };
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((ev) => frame.addEventListener(ev, endDrag));
    handle.addEventListener('keydown', (e) => {
      const step = e.shiftKey ? 10 : 2;
      let handled = true;
      switch (e.key) {
        case 'ArrowLeft': pos = Math.max(0, pos - step); break;
        case 'ArrowRight': pos = Math.min(100, pos + step); break;
        case 'Home': pos = 0; break;
        case 'End': pos = 100; break;
        default: handled = false;
      }
      if (handled) { e.preventDefault(); apply(); }
    });
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const hint = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          hint.disconnect();
          const at = (t, v) => setTimeout(() => { if (!dragging) { pos = v; apply(); } }, t);
          at(400, 68); at(1000, 34); at(1600, 50);
        });
      }, { threshold: 0.45 });
      hint.observe(ba);
    }
    apply();
  });

  /* ---- Votos de utilidad en las resenas ---- */
  document.querySelectorAll('.useful-btn').forEach((btn) => {
    const key = 'lunelle_useful_' + btn.dataset.useful;
    const base = parseInt(btn.dataset.base, 10) || 0;
    const countEl = btn.querySelector('.useful-count');
    const sync = () => {
      let active = false;
      try { active = localStorage.getItem(key) === '1'; } catch {}
      if (countEl) countEl.textContent = active ? base + 1 : base;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    };
    sync();
    btn.addEventListener('click', () => {
      try {
        if (localStorage.getItem(key) === '1') localStorage.removeItem(key);
        else localStorage.setItem(key, '1');
      } catch {}
      sync();
    });
  });

  /* ---- Countdown ---- */
  const countdownEls = document.querySelectorAll('[data-countdown]');
  if (countdownEls.length) {
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      const now = new Date();
      const end = new Date(now); end.setHours(23, 59, 59, 999);
      let diff = Math.max(0, end - now);
      const h = Math.floor(diff / 3600000); diff -= h * 3600000;
      const m = Math.floor(diff / 60000); diff -= m * 60000;
      const s = Math.floor(diff / 1000);
      const txt = pad(h) + ':' + pad(m) + ':' + pad(s);
      countdownEls.forEach((el) => { el.textContent = txt; });
    };
    tick(); setInterval(tick, 1000);
  }

  /* ---- Sticky bar ----
     Appears once the reader is past the element marked [data-sticky-after]
     (the trust row that closes the buybox), so it never competes with the
     buybox's own CTA. */
  const stickyBar = document.querySelector('.sticky-bar');
  const stickyAnchor = document.querySelector('[data-sticky-after]')
    || document.querySelector('.atc-wrap')
    || document.querySelector('.buybox');
  if (stickyBar && stickyAnchor) {
    const check = () => {
      const passed = stickyAnchor.getBoundingClientRect().bottom < 0;
      const nearFooter = window.innerHeight + window.scrollY > document.documentElement.scrollHeight - 200;
      stickyBar.classList.toggle('visible', passed && !nearFooter);
    };
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check, { passive: true });
    check();
  }

  /* ---- Horizontally scrollable boxes (the comparison table) ----
     Whether the table overflows depends on the table's own width, which no
     media query can see, so the arrows are switched on at runtime. */
  document.querySelectorAll('[data-hscroll]').forEach((wrap) => {
    const box = wrap.querySelector('[data-hscroll-box]');
    const prev = wrap.querySelector('[data-hscroll-prev]');
    const next = wrap.querySelector('[data-hscroll-next]');
    if (!box || !prev || !next) return;

    const update = () => {
      const room = box.scrollWidth - box.clientWidth;
      wrap.classList.toggle('is-scrollable', room > 4);
      prev.disabled = box.scrollLeft <= 2;
      next.disabled = box.scrollLeft >= room - 2;
    };
    /* Just under a full screen, so a column stays on-screen as an anchor. */
    const nudge = (dir) => box.scrollBy({ left: dir * box.clientWidth * 0.8, behavior: 'smooth' });
    prev.addEventListener('click', () => nudge(-1));
    next.addEventListener('click', () => nudge(1));
    box.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    if (window.ResizeObserver) new ResizeObserver(update).observe(box);
    update();
  });

  /* ---- In-page links ----
     Native smooth scrolling takes about the same half second whatever the
     distance, so a jump from the buybox to the reviews 10,000px below reads
     as a cut. This flies through it instead, with the time tied to the trip. */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  function glideTo(target) {
    const navH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nav-h')) || 72;
    const from = window.scrollY;
    const limit = document.documentElement.scrollHeight - window.innerHeight;
    const to = Math.max(0, Math.min(limit, target.getBoundingClientRect().top + from - navH - 16));
    const trip = Math.abs(to - from);
    if (trip < 4) return;
    if (reduceMotion.matches) { window.scrollTo(0, to); return; }
    const ms = Math.min(1400, Math.max(420, trip * 0.5));
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - t0) / ms);
      window.scrollTo(0, from + (to - from) * ease(p));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link || link.hasAttribute('data-no-glide')) return;
    const hash = link.getAttribute('href');
    if (!hash || hash === '#') return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;
    e.preventDefault();
    glideTo(target);
    history.replaceState(null, '', hash);
  });

  /* ---- Paginación de las reseñas ----
     Los controles se construyen aquí y no en el HTML: así no hay dos copias
     del mismo marcado que mantener, y si algún día hay tres reseñas o menos,
     no aparece una paginación de una sola página. */
  (() => {
    const lista = document.querySelector('.reviews-list');
    if (!lista) return;
    const fichas = [...lista.querySelectorAll('.review-full')];
    /* La primera pagina ensena UNA sola resena: al llegar a la seccion se ve
       el titular, la nota media y una resena entera, no un muro. De la segunda
       en adelante van de dos en dos, como antes. */
    const PRIMERA = 1, RESTO = 2;
    const rangos = [];
    for (let i = 0; i < fichas.length;) {
      const tam = rangos.length === 0 ? PRIMERA : RESTO;
      rangos.push([i, Math.min(i + tam, fichas.length)]);
      i += tam;
    }
    const paginas = rangos.length;
    if (paginas < 2) return;
    let actual = 0;

    const nav = document.createElement('nav');
    nav.className = 'review-pager';
    nav.setAttribute('aria-label', 'Reviews pages');
    const flecha = (dir, etiqueta) => {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'review-pager-btn';
      b.setAttribute('aria-label', etiqueta);
      b.innerHTML = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M' +
        (dir < 0 ? '15 5l-7 7 7 7' : '9 5l7 7-7 7') + '"/></svg>';
      b.addEventListener('click', () => ir(actual + dir));
      return b;
    };
    const prev = flecha(-1, 'Previous page');
    const next = flecha(1, 'Next page');
    const nums = document.createElement('div');
    nums.className = 'review-pager-nums';
    const botones = [];
    for (let i = 0; i < paginas; i++) {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'review-pager-num'; b.textContent = String(i + 1);
      b.addEventListener('click', () => ir(i));
      nums.appendChild(b); botones.push(b);
    }
    const cuenta = document.createElement('span');
    cuenta.className = 'review-pager-count';
    /* Botón decorativo: no hace nada a propósito, así que no se le engancha
       ningún listener. Va con aria-disabled para no anunciarle a un lector de
       pantalla una acción que no existe, y fuera del orden de tabulación. */
    const mas = document.createElement('button');
    mas.type = 'button';
    mas.className = 'review-pager-more';
    mas.setAttribute('aria-disabled', 'true');
    mas.tabIndex = -1;
    mas.innerHTML = '<span>See more reviews</span>' +
      '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>';

    nav.append(prev, nums, next, cuenta, mas);
    lista.appendChild(nav);

    function ir(p, mover) {
      actual = Math.max(0, Math.min(paginas - 1, p));
      const desde = rangos[actual][0], hasta = rangos[actual][1];
      fichas.forEach((f, i) => { f.hidden = i < desde || i >= hasta; });
      botones.forEach((b, i) => {
        b.classList.toggle('is-active', i === actual);
        b.setAttribute('aria-current', i === actual ? 'page' : 'false');
      });
      prev.disabled = actual === 0;
      next.disabled = actual === paginas - 1;
      /* El boton decorativo de "See more reviews" solo aparece al final: en una
         pagina intermedia invita a algo que el paginador ya esta ofreciendo. */
      mas.hidden = actual !== paginas - 1;
      /* Una sola resena se leia como "5-5 of 5", asi que en ese caso va sola.
         Pasa en la primera pagina y puede pasar tambien en la ultima. */
      cuenta.textContent = (hasta - desde === 1 ? hasta : (desde + 1) + '–' + hasta) + ' of ' + fichas.length;
      /* Al cambiar de página se sube al principio de la lista: si no, te quedas
         mirando el final de una reseña que ya no es la que estabas leyendo. */
      if (mover !== false) {
        const y = lista.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
    ir(0, false);
  })();

  /* ---- Carrusel de reseñas ----
     Avanza solo cada 7 s, se para al tocarlo o al pasar por encima, y se
     detiene del todo cuando la sección no está en pantalla: no tiene sentido
     gastar temporizadores en algo que nadie está mirando. */
  document.querySelectorAll('[data-say-carousel]').forEach((car) => {
    const slides = [...car.querySelectorAll('[data-say-slide]')];
    const dots = [...car.querySelectorAll('[data-say-dot]')];
    if (slides.length < 2) return;

    let actual = 0, timer = null, visible = false;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

    const pintar = (i) => {
      actual = (i + slides.length) % slides.length;
      slides.forEach((s, n) => s.classList.toggle('is-active', n === actual));
      dots.forEach((d, n) => {
        d.classList.toggle('is-active', n === actual);
        d.setAttribute('aria-selected', n === actual ? 'true' : 'false');
      });
    };
    const parar = () => { clearInterval(timer); timer = null; };
    const arrancar = () => {
      parar();
      if (!visible || reduce.matches) return;
      timer = setInterval(() => pintar(actual + 1), 7000);
    };
    const ir = (i) => { pintar(i); arrancar(); };

    car.querySelector('[data-say-prev]').addEventListener('click', () => ir(actual - 1));
    car.querySelector('[data-say-next]').addEventListener('click', () => ir(actual + 1));
    dots.forEach((d, n) => d.addEventListener('click', () => ir(n)));
    car.addEventListener('mouseenter', parar);
    car.addEventListener('mouseleave', arrancar);
    car.addEventListener('touchstart', parar, { passive: true });

    /* Deslizar con el dedo, igual que en la galería */
    let x0 = null, y0 = null;
    car.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      x0 = e.clientX; y0 = e.clientY;
    });
    car.addEventListener('pointermove', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0, dy = e.clientY - y0;
      if (Math.abs(dy) > Math.abs(dx)) { x0 = null; return; }
      if (Math.abs(dx) < 45) return;
      x0 = null;
      ir(actual + (dx < 0 ? 1 : -1));
    });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach((ev) =>
      car.addEventListener(ev, () => { x0 = null; }));

    new IntersectionObserver((entradas) => {
      visible = entradas[0].isIntersecting;
      if (visible) arrancar(); else parar();
    }, { threshold: 0.25 }).observe(car);

    pintar(0);
  });

  /* ---- Purchase notifications ---- */
  const notifEl = document.querySelector('[data-purchase-notif]');
  if (notifEl) {
    /* Sophie M. vivía en Darwin aquí y en Brisbane en su reseña: cada nombre
       aparece en una sola ciudad, y ninguno coincide con los de las reseñas. */
    const people = [
      { initial: 'A', name: 'Ava T.',      city: 'Gold Coast' },  { initial: 'Z', name: 'Zoe S.',      city: 'Adelaide' },
      { initial: 'I', name: 'Isabella C.', city: 'Brisbane' },    { initial: 'G', name: 'Grace V.',    city: 'Canberra' },
      { initial: 'O', name: 'Olivia H.',   city: 'Hobart' },      { initial: 'E', name: 'Emma L.',     city: 'Newcastle' },
      { initial: 'H', name: 'Harper W.',   city: 'Wollongong' },  { initial: 'L', name: 'Lily N.',     city: 'Geelong' },
      { initial: 'J', name: 'Jasmine P.',  city: 'Darwin' },      { initial: 'C', name: 'Charlotte B.', city: 'Cairns' },
      { initial: 'M', name: 'Matilda R.',  city: 'Ballarat' },    { initial: 'E', name: 'Ella W.',     city: 'Townsville' },
      { initial: 'A', name: 'Amelia J.',   city: 'Toowoomba' },   { initial: 'S', name: 'Scarlett D.', city: 'Bendigo' },
      { initial: 'W', name: 'Willow K.',   city: 'Launceston' },  { initial: 'F', name: 'Freya M.',    city: 'Mackay' },
      { initial: 'I', name: 'Ivy N.',      city: 'Rockhampton' }, { initial: 'P', name: 'Poppy S.',    city: 'Bunbury' },
      { initial: 'H', name: 'Hazel T.',    city: 'Coffs Harbour' },{ initial: 'M', name: 'Maya L.',    city: 'Albury' },
      { initial: 'A', name: 'Aisha R.',    city: 'Bendigo' },     { initial: 'L', name: 'Lucia F.',    city: 'Wagga Wagga' },
      { initial: 'S', name: 'Sienna H.',   city: 'Shepparton' },  { initial: 'N', name: 'Nina O.',     city: 'Mandurah' },
      { initial: 'T', name: 'Tara V.',     city: 'Bundaberg' },   { initial: 'R', name: 'Riley A.',    city: 'Hervey Bay' },
      { initial: 'D', name: 'Daisy C.',    city: 'Gladstone' },   { initial: 'E', name: 'Eloise G.',   city: 'Mildura' },
      { initial: 'K', name: 'Keira B.',    city: 'Tamworth' },    { initial: 'A', name: 'Anika D.',    city: 'Orange' },
      { initial: 'B', name: 'Bella M.',    city: 'Dubbo' },       { initial: 'V', name: 'Violet P.',   city: 'Warrnambool' },
    ];
    const packs = ['1 kit', '2 kits', '3 kits'];
    /* Ventana de casi dos días: con visitas repetidas, un "hace 2 minutos"
       cada vez cantaba más que el propio aviso. */
    const times = [
      /* Solo dos marcas en minutos, y las dos casi en la hora: un "hace 5
         minutos" en una tienda recién abierta es lo que delata el invento. */
      '45 minutes ago', '50 minutes ago',
      '2 hours ago', '3 hours ago', '5 hours ago', '6 hours ago',
      '8 hours ago', '9 hours ago', '11 hours ago', '13 hours ago',
      '14 hours ago', '16 hours ago', '17 hours ago', '19 hours ago',
      '21 hours ago', '22 hours ago', '24 hours ago', '26 hours ago',
      '28 hours ago', '31 hours ago', '33 hours ago', '35 hours ago',
      '38 hours ago', '41 hours ago', '43 hours ago', '46 hours ago',
    ];
    /* Baraja sin reposición: no repite un nombre hasta agotar la lista, así que
       nadie ve dos veces a la misma persona en la misma sesión. */
    const sinRepetir = (arr) => {
      let bolsa = [];
      return () => {
        if (!bolsa.length) {
          bolsa = arr.slice();
          for (let i = bolsa.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [bolsa[i], bolsa[j]] = [bolsa[j], bolsa[i]];
          }
        }
        return bolsa.pop();
      };
    };
    const siguientePersona = sinRepetir(people);
    const siguienteTiempo = sinRepetir(times);
    const $ = (s) => notifEl.querySelector(s);
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
    let dismissTimer = null, nextTimer = null, closed = false;
    const hide = () => { notifEl.classList.remove('show'); notifEl.setAttribute('aria-hidden', 'true'); };
    const showRandom = () => {
      if (closed) return;
      const p = siguientePersona();
      $('[data-purchase-avatar]').textContent = p.initial;
      $('[data-purchase-name]').textContent = p.name;
      $('[data-purchase-city]').textContent = p.city;
      $('[data-purchase-pack]').textContent = pick(packs);
      $('[data-purchase-time]').textContent = siguienteTiempo();
      notifEl.classList.add('show');
      notifEl.setAttribute('aria-hidden', 'false');
      clearTimeout(dismissTimer);
      dismissTimer = setTimeout(hide, 5500);
      clearTimeout(nextTimer);
      nextTimer = setTimeout(showRandom, 14000 + Math.random() * 16000);
    };
    const closeBtn = $('[data-purchase-close]');
    if (closeBtn) closeBtn.addEventListener('click', () => { closed = true; hide(); clearTimeout(nextTimer); clearTimeout(dismissTimer); });
    setTimeout(showRandom, 6000);
  }

  /* ========================================================
     7. PDP — bundle picker + add to cart
     ======================================================== */
  const offerInputs = document.querySelectorAll('.offer-block input[name="bundle"]');
  const priceNowEl = document.querySelector('.price-now');
  const priceOldEl = document.querySelector('.price-old');
  const savingsEl = document.querySelector('.savings-badge');
  const giftNameEl = document.querySelector('[data-gift-name]');
  const giftWasEl = document.querySelector('[data-gift-was]');
  /* Lo que costaria un gorro suelto. Vive aqui y no repartido por el HTML para
     que la ficha y las filas del bundle no puedan decir cifras distintas. */
  const BONNET_UNIT = 16;
  const priceKitsEl = document.querySelector('[data-price-kits]');
  const atcLabelEl = document.querySelector('[data-atc-label]');
  const quickBuyPrice = document.querySelector('.quick-buy .price-inline');
  const priceUnitEl = document.querySelector('[data-price-unit]');
  const stickyNow = document.querySelector('.sticky-bar .price strong');
  const stickyOld = document.querySelector('.sticky-bar .price span');
  const stickyName = document.querySelector('.sticky-bar .name');

  /* Apple Pay y Google Pay compran una variante, no el carrito: si el visitante
     cambia de pack o de color hay que reapuntar el input oculto del formulario,
     porque si no pagaria la variante con la que se cargo la pagina.
     Si la combinacion no existe en la tabla, se esconde el boton: mejor no
     ofrecer pago rapido que cobrar el articulo equivocado. */
  /* El boton de pago rapido solo se ensena si Shopify va a pintar un monedero
     de verdad. La recomendacion viene escrita por el servidor en el atributo
     'recommended' del componente: apple_pay o google_pay segun dispositivo, y
     null cuando no hay monedero para ese visitante (entonces pintaria un
     "Buy it now" sin marca que ademas, desde un pais sin zona de envio, muere
     con "transaction failed"). En ese caso, mejor ningun boton. */
  function monederoRecomendado() {
    const n = document.querySelector('shopify-accelerated-checkout');
    if (!n) return null;
    try {
      const r = JSON.parse(n.getAttribute('recommended') || 'null');
      return r && r.name ? r.name : null;
    } catch { return null; }
  }

  function syncExpressVariant() {
    const input = document.querySelector('[data-express-variant]');
    if (!input) return;
    const caja = document.querySelector('[data-express]');
    const monedero = monederoRecomendado();
    const conMarca = monedero === 'apple_pay' || monedero === 'google_pay';
    const v = selectedVariant();
    const id = SHOPIFY_VARIANTS[variantKey({ id: v.id, colours: selectedColours(v) })];
    if (id && conMarca) { input.value = id; if (caja) caja.hidden = false; }
    else if (caja) { caja.hidden = true; }
  }

  function selectedVariant() {
    const checked = document.querySelector('.offer-block input[name="bundle"]:checked');
    return VARIANTS[(checked && checked.value) || 'pack-1'] || VARIANTS['pack-1'];
  }

  function syncOfferPrices() {
    const v = selectedVariant();
    const savings = Math.max(0, v.compare - v.price);
    if (priceNowEl) priceNowEl.textContent = money(v.price);
    /* Solo se tacha si hay algo que tachar: con un kit el compare es el propio
       precio, y "$34.95 $34.95" no dice nada. */
    if (priceOldEl) priceOldEl.textContent = v.compare > v.price ? money(v.compare) : '';
    if (savingsEl) {
      savingsEl.textContent = savings > 0 ? 'You save ' + money(savings) : '';
      savingsEl.hidden = savings <= 0;
    }
    /* The big number is the price of the whole bundle, so say how many kits
       it buys — otherwise $52.45 reads as the price of one. */
    /* Un gorro por kit, asi que el nombre y el valor siguen al pack elegido. */
    if (giftNameEl) giftNameEl.textContent = v.kits === 1 ? 'Satin sleep bonnet' : v.kits + ' × satin sleep bonnet';
    if (giftWasEl) giftWasEl.textContent = 'A$' + BONNET_UNIT * v.kits;
    if (priceKitsEl) priceKitsEl.textContent = 'Total for ' + v.kits + (v.kits === 1 ? ' kit' : ' kits');
    if (atcLabelEl) atcLabelEl.textContent = 'Add to cart — ' + money(v.price);
    /* Con el pack de 2 por defecto, el primer número de la página era 74,95 sin
       decir de qué. Ahora la cantidad va delante del precio. */
    if (quickBuyPrice) quickBuyPrice.textContent = '· ' + v.label + ' — ' + money(v.price);
    if (priceUnitEl) priceUnitEl.textContent = v.kits > 1 ? money(v.price / v.kits) + ' per kit' : '';
    if (stickyNow) stickyNow.textContent = money(v.price);
    if (stickyOld) stickyOld.textContent = money(v.compare);
    if (stickyName) stickyName.textContent = PRODUCT.title + ' · ' + v.label;
  }
  /* ---- Colour picker: one row per kit in the chosen bundle ---- */
  const colourPicker = document.querySelector('[data-colour-picker]');
  const colourRowsEl = document.querySelector('[data-colour-rows]');
  const colourHintEl = document.querySelector('[data-colour-hint]');
  /* Choices survive switching bundle: going 3 kits → 2 keeps the first two. */
  const colourChoices = [DEFAULT_COLOUR, DEFAULT_COLOUR, DEFAULT_COLOUR];

  function renderColourRows() {
    if (!colourRowsEl) return;
    const kits = selectedVariant().kits;
    if (colourHintEl) {
      colourHintEl.textContent = kits > 1 ? 'Pick a colour for each of your ' + kits + ' kits' : 'Each kit ships with a matching satin bonnet';
    }
    colourRowsEl.innerHTML = Array.from({ length: kits }, (_, idx) => {
      const current = colourChoices[idx] || DEFAULT_COLOUR;
      const opts = COLOURS.map((c) => (
        '<label class="colour-opt' + (c.id === current ? ' is-active' : '') + '">' +
          '<input type="radio" name="colour-' + idx + '" value="' + c.id + '" data-colour-index="' + idx + '"' + (c.id === current ? ' checked' : '') + '>' +
          '<img src="' + c.image + '" alt="' + c.label + '" width="240" height="300" loading="lazy">' +
          '<span class="colour-opt-name">' + c.label + '</span>' +
        '</label>'
      )).join('');
      return '<div class="colour-row" role="radiogroup" aria-label="Colour for kit ' + (idx + 1) + '">' +
        (kits > 1 ? '<span class="colour-row-label">Kit ' + (idx + 1) + '</span>' : '') +
        '<div class="colour-opts">' + opts + '</div>' +
      '</div>';
    }).join('');
    colourPicker && colourPicker.classList.toggle('is-multi', kits > 1);
  }

  if (colourRowsEl) {
    colourRowsEl.addEventListener('change', (e) => {
      const input = e.target.closest('input[data-colour-index]');
      if (!input) return;
      colourChoices[Number(input.dataset.colourIndex)] = input.value;
      input.closest('.colour-row').querySelectorAll('.colour-opt').forEach((l) => {
        l.classList.toggle('is-active', l.contains(input) || l.querySelector('input').checked);
      });
      /* The gallery opens on the blush kit, so only a different colour needs
         the last shot — the one with all three and their bonnets. Clicking
         the thumb reuses the gallery's own swap: fade, alt text and all. */
      if (input.value !== DEFAULT_COLOUR) {
        const thumbs = document.querySelectorAll('.gallery-thumb');
        const last = thumbs[thumbs.length - 1];
        if (last && !last.classList.contains('active')) last.click();
      }
      /* el color forma parte de la variante: sin esto, quien elige burdeos y
         paga con Apple Pay se lleva el rosa */
      syncExpressVariant();
    });
    renderColourRows();
    syncExpressVariant();
  }

  function selectedColours(variant) {
    return normaliseColours(colourChoices.slice(0, variant.kits), variant.kits);
  }

  offerInputs.forEach((i) => i.addEventListener('change', () => { syncOfferPrices(); renderColourRows(); syncExpressVariant(); }));
  if (offerInputs.length) syncOfferPrices();
  syncExpressVariant();

  document.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const v = btn.dataset.variantId ? (VARIANTS[btn.dataset.variantId] || selectedVariant()) : selectedVariant();
      const goToCart = btn.hasAttribute('data-buy-now');
      const labelEl = btn.querySelector('[data-atc-label]');
      const before = labelEl ? labelEl.textContent : btn.textContent;

      Cart.add(v.id, 1, selectedColours(v));

      /* Avisar a Shopify AHORA, no al ir a pagar. El carrito de esta ficha vive
         en el navegador, asi que sin esta llamada Shopify no se enteraba de que
         alguien anade hasta que pulsaba Checkout, y el evento "anadir al
         carrito" llegaba a Meta en el instante de empezar el pago.

         Va sin await y con el error tragado a proposito: esto es telemetria y
         no puede estropear ni la interfaz ni la compra. handOffToShopify sigue
         vaciando y reconstruyendo el carrito antes de pagar, asi que lo que se
         cobra no depende de esto.

         En "Buy now" se salta: alli el traspaso ocurre acto seguido y el evento
         saldria dos veces. */
      if (!goToCart) {
        const shopifyVariant = SHOPIFY_VARIANTS[variantKey({ id: v.id, colours: selectedColours(v) })];
        if (shopifyVariant) postJson('/cart/add.js', { id: shopifyVariant, quantity: 1 }).catch(() => {});
      }

      btn.classList.add('added');
      if (labelEl) labelEl.textContent = 'Added ✓'; else btn.textContent = 'Added ✓';

      /* "Buy now" goes straight to checkout; everything else slides the
         drawer open so the reader never loses their place. */
      if (goToCart) {
        setTimeout(() => {
          handOffToShopify()
            .then(() => { window.location.href = '/checkout'; })
            /* If the hand-off fails, the cart is the honest place to land:
               the reader sees what they have instead of an empty checkout. */
            .catch(() => { window.location.href = '/cart'; });
        }, 300);
        return;
      }
      setTimeout(openDrawer, 260);
      setTimeout(() => {
        btn.classList.remove('added');
        if (labelEl) labelEl.textContent = before; else btn.textContent = before;
        syncOfferPrices();
      }, 1500);
    });
  });

  /* ========================================================
     8. CART PAGE
     ======================================================== */
  const svgTruck = '<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#43273A" stroke-width="1.8"><path d="M3 7h11v9H3z"/><path d="M14 10h5l3 3v3h-8"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg>';
  const svgBonnet = '<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#43273A" stroke-width="1.8"><path d="M4 14c0-5 3.6-9 8-9s8 4 8 9c0 2-1 3.5-2.5 3.5H6.5C5 17.5 4 16 4 14z"/><path d="M4.5 16.5c2.5 1.6 12.5 1.6 15 0"/></svg>';

  /* Builds the whole cart body. `drawer` swaps the header's close control:
     the page needs a link back to the product, the drawer needs a button. */
  function buildCartHtml(drawer) {
      const cart = Cart.get();
      const t = Cart.totals(cart);
      const closeCtl = drawer
        ? '<button type="button" class="cart-close" data-cart-close aria-label="Close cart">' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button>'
        : '<a href="/products/lunelle-heatless-curling-kit" class="cart-close" aria-label="Close cart">' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></a>';
      const head = '<header class="cart-card-head"><h1>Cart <span class="cart-card-count">(' + Cart.count(cart) + ')</span></h1>' +
        closeCtl + '</header>';

      if (t.empty) {
        return head +
          '<div class="cart-empty"><h3>Your cart is empty</h3>' +
          '<p>You haven\'t added the Lunelle Heatless Curling Kit yet. Pick your bundle and wake up with curls.</p>' +
          '<a class="btn btn-primary btn-lg" href="/products/lunelle-heatless-curling-kit#buybox">Shop the kit — ' + money(BASE_UNIT) + '</a></div>';
      }

      const lines = cart.items.map((i) => {
        const v = VARIANTS[i.id];
        const linePrice = v.price * i.qty;
        const lineCompare = v.compare * i.qty;
        const saving = Math.max(0, lineCompare - linePrice);
        const kits = v.kits * i.qty;
        const perKit = linePrice / kits;
        const pct = Math.round(((BASE_UNIT - perKit) / BASE_UNIT) * 100);
        const key = lineKey(i);
        /* Chips live on their own full-width row below the line: inside the
           info column they were narrow enough to stack one per line. */
        /* Los gorros ya se multiplicaban por i.qty; los chips no, así que dos
           packs de 2 enseñaban dos colores para cuatro kits. */
        const todosLosColores = Array.from({ length: i.qty }, () => i.colours).flat();
        const chips = todosLosColores.map((cid, n) => {
          const c = colourById(cid);
          return '<span class="cart-colour-chip" title="' + (kits > 1 ? 'Kit ' + (n + 1) + ': ' : '') + c.label + '">' +
            '<img src="' + c.image + '" alt="" width="240" height="300">' +
            (kits > 1 ? '<i class="chip-kit">' + (n + 1) + '</i>' : '') +
            '<span class="chip-name">' + c.label + '</span></span>';
        }).join('');
        return '<div class="cart-line"><div class="cart-line-main">' +
          '<div class="cart-line-media"><img src="' + PRODUCT.image + '" alt="' + PRODUCT.title + '" width="88" height="88"></div>' +
          '<div class="cart-line-info">' +
            '<div class="cart-line-title">' + PRODUCT.title + '</div>' +
            '<div class="cart-line-meta">' + (i.qty > 1 ? i.qty + ' × ' + v.label + ' = ' + kits + ' kits' : v.label) + ' · satin</div>' +
            '<div class="qty-control" aria-label="Quantity">' +
              '<button class="qty-btn" data-qty="minus" data-id="' + key + '" aria-label="Decrease quantity">−</button>' +
              '<span class="qty-value">' + i.qty + '</span>' +
              '<button class="qty-btn" data-qty="plus" data-id="' + key + '" aria-label="Increase quantity">+</button>' +
            '</div>' +
          '</div>' +
          '<div class="cart-line-right">' +
            '<button class="cart-line-remove" data-remove="' + key + '" aria-label="Remove ' + v.label + '">' +
            '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13"/></svg></button>' +
            '<div class="cart-line-prices"><s>' + money(lineCompare) + '</s><b>' + money(linePrice) + '</b></div>' +
            (saving > 0 ? '<div class="cart-line-saving">(You save ' + money(saving) + ')</div>' : '') +
          '</div></div>' +
          '<div class="cart-colour-chips">' + chips + '</div>' +
          (kits > 1 && perKit < BASE_UNIT
            ? '<span class="bundle-pill">Bundle price · Save ' + pct + '% — ' + money(perKit) + '/kit</span>' : '') +
        '</div>';
      }).join('');

      /* Sin cifra tachada: no se ha cobrado nunca por el envio ni por el gorro,
         asi que cualquier precio ahi seria inventado. Van gratis y ya esta. */
      const freeLine = (tag, icon, title, meta, footer) =>
        '<div class="cart-line cart-line--free"><div class="cart-line-main">' +
          '<div class="freebie cart-freebie"><span class="freebie-tag">FREE</span>' + icon + '</div>' +
          '<div class="cart-line-info"><div class="cart-line-title">' + title + '</div>' + meta + '</div>' +
          '<div class="cart-line-right"><div class="cart-line-prices"><b class="is-free">' + money(0) + '</b></div></div>' +
        '</div>' + (footer || '') + '</div>';

      /* One bonnet per kit, each matching that kit's colour — so a 3-pack
         arrives with three bonnets, not one. */
      const bonnetColours = cart.items.flatMap((i) =>
        Array.from({ length: i.qty }, () => i.colours).flat()
      );
      const bonnetCount = bonnetColours.length;
      /* Dots only: the "× N" in the title already carries the count, so the
         labels would just repeat the kit line above. */
      const bonnetChips = bonnetColours.map((cid) => {
        const c = colourById(cid);
        return '<span class="cart-colour-dot" title="' + c.label + '">' +
          '<img src="' + c.image + '" alt="' + c.label + '" width="240" height="300"></span>';
      }).join('');
      /* Dots sit inside the info column so they line up under the title
         rather than floating under the icon. */
      const bonnetMeta = '<div class="cart-line-meta">One per kit, matching each colour</div>' +
        '<div class="cart-colour-dots">' + bonnetChips + '</div>';

      return head +
        '<div class="cart-lines">' + lines +
          freeLine('ship', svgTruck, 'Free Shipping', '<div class="cart-line-meta">Australia-wide · tracked</div>') +
          freeLine('gift', svgBonnet,
            'Satin Sleep Bonnet' + (bonnetCount > 1 ? ' × ' + bonnetCount : ''),
            bonnetMeta) +
        '</div>' +
        '<div class="cart-protection">' +
          '<span class="cart-protection-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E8477E" stroke-width="2"><path d="M12 3l7 3v6c0 4.4-3 7.5-7 9-4-1.5-7-4.6-7-9V6z"/><path d="M9.5 12l1.8 1.8L15 10"/></svg></span>' +
          '<span class="cart-protection-text"><strong>Shipping Protection</strong><span>Instant reshipment if lost or damaged</span></span>' +
          '<span class="cart-protection-price">' + money(PROTECTION.price) + '</span>' +
          '<button type="button" class="switch' + (cart.protection ? ' on' : '') + '" data-protection role="switch" aria-checked="' + (cart.protection ? 'true' : 'false') + '" aria-label="Add shipping protection"><span class="switch-knob"></span></button>' +
        '</div>' +
        '<div class="cart-totals">' +
          (t.savings > 0 ? '<div class="cart-savings-row"><span>Savings</span><span>-' + money(t.savings) + '</span></div>' : '') +
          '<div class="cart-subtotal-row"><span>Subtotal</span><span>' + money(t.subtotal) + '</span></div>' +
          '<button type="button" class="btn btn-primary btn-lg btn-block cart-checkout">Checkout</button>' +
          /* Copiados tal cual del bloque de la ficha por fix-cart-and-dark.js.
             No editar a mano: se edita product-pdp.liquid y se vuelve a correr. */
          '<div class="pay-badges" aria-label="Accepted payment methods">' +
            '<span class="pay-badge visa" title="Visa">VISA</span>' +
            '<span class="pay-badge mc" title="Mastercard"><i></i><i></i></span>' +
            '<span class="pay-badge amex" title="American Express">AMEX</span>' +
            '<span class="pay-badge apple" title="Apple Pay"><svg width="11" height="13" viewBox="0 0 170 170" fill="currentColor" aria-hidden="true"><path d="M150.4 130.3c-2.5 5.7-5.4 10.9-8.7 15.7-4.6 6.5-8.3 11-11.2 13.6-4.5 4.1-9.3 6.2-14.4 6.3-3.7 0-8.1-1-13.3-3.2-5.2-2.1-10-3.2-14.4-3.2-4.6 0-9.5 1.1-14.7 3.2-5.3 2.1-9.5 3.2-12.8 3.4-4.9 .2-9.8-2-14.7-6.5-3.1-2.7-7-7.4-11.7-14-5-7.1-9.2-15.3-12.4-24.7C18.7 110.8 17 101 17 91.5c0-10.9 2.3-20.2 7-28.1 3.7-6.3 8.6-11.3 14.8-14.9C45 44.8 51.6 43 58.8 42.9c3.9 0 9 1.2 15.4 3.6 6.4 2.4 10.4 3.6 12.2 3.6 1.3 0 5.9-1.4 13.6-4.2 7.3-2.6 13.4-3.7 18.4-3.3 13.6 1.1 23.9 6.5 30.7 16.2-12.2 7.4-18.2 17.7-18.1 31 .1 10.3 3.9 18.9 11.2 25.8 3.3 3.2 7.1 5.6 11.2 7.4-.9 2.6-1.8 5.1-2.9 7.5zM119.1 7.2c0 8.1-3 15.7-8.9 22.7-7.1 8.3-15.7 13.1-25.1 12.4-.1-1-.2-2-.2-3.1 0-7.8 3.4-16.1 9.4-22.9 3-3.4 6.8-6.3 11.5-8.6C110.4 5.5 114.8 4.2 118.9 4c.1 1.1.2 2.2.2 3.2z"/></svg>&nbsp;Pay</span>' +
            '<span class="pay-badge gpay" title="Google Pay"><svg width="13" height="13" viewBox="0 0 48 48" aria-hidden="true"><path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/><path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/><path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/><path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/></svg>&nbsp;Pay</span>' +
          '</div>' +
          '<p class="cart-note">Secure SSL checkout · 30-day returns</p>' +
        '</div>';
  }

  /* Same click behaviour wherever the cart is rendered. */
  function bindCartControls(root, rerender) {
    root.addEventListener('click', (e) => {
      const qtyBtn = e.target.closest('[data-qty]');
      const removeBtn = e.target.closest('[data-remove]');
      const protBtn = e.target.closest('[data-protection]');
      if (qtyBtn) {
        const cart = Cart.get();
        const line = cart.items.find((i) => lineKey(i) === qtyBtn.dataset.id);
        if (!line) return;
        Cart.setQty(qtyBtn.dataset.id, qtyBtn.dataset.qty === 'plus' ? line.qty + 1 : line.qty - 1);
        rerender();
      } else if (removeBtn) {
        Cart.remove(removeBtn.dataset.remove);
        rerender();
      } else if (protBtn) {
        Cart.setProtection(!protBtn.classList.contains('on'));
        rerender();
      }
    });
  }

  const cartRoot = document.querySelector('[data-cart-root]');
  if (cartRoot) {
    const renderCartPage = () => { cartRoot.innerHTML = buildCartHtml(false); };
    bindCartControls(cartRoot, renderCartPage);
    renderCartPage();
  }

  /* ========================================================
     8b. CART DRAWER
     Slides in from the right on desktop, covers the screen on
     mobile. Adding to cart opens it instead of leaving the page.
     ======================================================== */
  const drawer = document.createElement('div');
  drawer.className = 'cart-drawer';
  drawer.setAttribute('data-cart-drawer', '');
  drawer.setAttribute('aria-hidden', 'true');
  drawer.innerHTML =
    '<div class="cart-drawer-scrim" data-cart-close></div>' +
    '<aside class="cart-drawer-panel" role="dialog" aria-modal="true" aria-label="Your cart">' +
      '<div class="cart-drawer-body" data-cart-drawer-body></div>' +
    '</aside>';
  document.body.appendChild(drawer);

  const drawerBody = drawer.querySelector('[data-cart-drawer-body]');
  const renderDrawer = () => { drawerBody.innerHTML = buildCartHtml(true); };
  let lastFocus = null;

  function openDrawer() {
    lastFocus = document.activeElement;
    renderDrawer();
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    const first = drawerBody.querySelector('[data-cart-close]');
    if (first) setTimeout(() => first.focus(), 60);
  }
  function closeDrawer() {
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-open');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  bindCartControls(drawerBody, renderDrawer);
  drawer.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-close]')) { e.preventDefault(); closeDrawer(); }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('open')) closeDrawer();
  });
  /* Keep the drawer honest if the cart changes from anywhere else. */
  document.addEventListener('lunelle:cart', () => {
    if (drawer.classList.contains('open')) renderDrawer();
  });

  /* The nav cart icon opens the drawer; the href stays as a no-JS fallback. */
  document.addEventListener('click', (e) => {
    const cartLink = e.target.closest('a[href="/cart"]');
    if (!cartLink || cartLink.hasAttribute('data-buy-now')) return;
    if (document.querySelector('[data-cart-root]')) return; // already on the cart page
    e.preventDefault();
    openDrawer();
  });

  window.LunelleCartDrawer = { open: openDrawer, close: closeDrawer };

  /* ========================================================
     9. CHECKOUT PAGE
     ======================================================== */
  const checkoutRoot = document.querySelector('[data-checkout]');
  if (checkoutRoot) {
    const summary = checkoutRoot.querySelector('[data-checkout-summary]');
    const form = checkoutRoot.querySelector('[data-checkout-form]');
    const done = document.querySelector('[data-order-done]');
    const grid = checkoutRoot.querySelector('.checkout-grid');

    function renderSummary() {
      const cart = Cart.get();
      const t = Cart.totals(cart);
      if (t.empty) {
        grid.innerHTML = '<div class="checkout-card" style="grid-column:1/-1"><div class="cart-empty">' +
          '<h3>Nothing to check out</h3><p>Your cart is empty — pick a bundle first.</p>' +
          '<a class="btn btn-primary btn-lg" href="/products/lunelle-heatless-curling-kit#buybox">Shop the kit — ' + money(BASE_UNIT) + '</a></div></div>';
        return;
      }
      const items = cart.items.map((i) => {
        const v = VARIANTS[i.id];
        const names = i.colours.map((cid) => colourById(cid).label).join(', ');
        return '<div class="sum-item"><img src="' + PRODUCT.image + '" alt=""><div class="sum-item-info">' +
          '<b>' + PRODUCT.title + '</b><span>' + v.label + ' × ' + i.qty + ' · ' + names + '</span></div>' +
          '<span>' + money(v.price * i.qty) + '</span></div>';
      }).join('');
      summary.innerHTML = '<h2>Order summary</h2><div class="sum-items">' + items +
        (cart.protection ? '<div class="sum-item"><span class="sum-item-icon" aria-hidden="true">' +
          '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E8477E" stroke-width="2"><path d="M12 3l7 3v6c0 4.4-3 7.5-7 9-4-1.5-7-4.6-7-9V6z"/><path d="M9.5 12l1.8 1.8L15 10"/></svg></span><div class="sum-item-info">' +
          '<b>Shipping Protection</b><span>Reshipment if lost or damaged</span></div><span>' + money(PROTECTION.price) + '</span></div>' : '') +
        '</div>' +
        '<div class="sum-line"><span>Satin Sleep Bonnet</span><span class="is-free">FREE</span></div>' +
        '<div class="sum-line"><span>Shipping (Australia-wide)</span><span class="is-free">FREE</span></div>' +
        (t.savings > 0 ? '<div class="sum-line"><span class="muted">Total savings</span><span class="muted">-' + money(t.savings) + '</span></div>' : '') +
        '<div class="sum-line total"><span>Total</span><span>' + money(t.subtotal) + '</span></div>' +
        '<p class="cart-note" style="margin-top:16px">Secure SSL checkout · 30-day returns</p>';
    }

    checkoutRoot.querySelectorAll('.pay-option input').forEach((input) => {
      const sync = () => checkoutRoot.querySelectorAll('.pay-option').forEach((o) =>
        o.classList.toggle('checked', o.contains(o.querySelector('input:checked'))));
      input.addEventListener('change', sync);
      sync();
    });

    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (Cart.totals().empty) return;
        const ref = 'LUN-' + String(Date.now()).slice(-6) + '-' + Math.floor(Math.random() * 900 + 100);
        const email = (form.querySelector('[name="email"]') || {}).value || '';
        Cart.clear();
        syncBadge();
        checkoutRoot.hidden = true;
        if (done) {
          done.hidden = false;
          const refEl = done.querySelector('[data-order-ref]');
          const mailEl = done.querySelector('[data-order-email]');
          if (refEl) refEl.textContent = ref;
          if (mailEl) mailEl.textContent = email;
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    }

    renderSummary();
    document.addEventListener('lunelle:cart', renderSummary);
  }
  /* Copiar el codigo de descuento. navigator.clipboard solo existe en HTTPS y
     algunos navegadores embebidos (Instagram, Facebook) lo bloquean, asi que
     hay un plan B con un textarea y execCommand, que sigue funcionando ahi. */
  document.addEventListener("click", function (ev) {
    const boton = ev.target.closest("[data-promo-copy]");
    if (!boton) return;
    const caja = boton.closest("[data-promo]") || document;
    const codigo = caja.querySelector("[data-promo-code]");
    if (!codigo) return;
    const texto = (codigo.textContent || "").trim();
    function avisar(ok) {
      boton.textContent = ok ? "Copied" : texto;
      boton.setAttribute("data-copied", "");
      setTimeout(function () { boton.textContent = "Copy"; boton.removeAttribute("data-copied"); }, 2000);
    }
    function plandeB() {
      try {
        const ta = document.createElement("textarea");
        ta.value = texto; ta.setAttribute("readonly", "");
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); ta.setSelectionRange(0, texto.length);
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        avisar(ok);
      } catch (e) { avisar(false); }
    }
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(texto).then(function () { avisar(true); }).catch(plandeB);
    } else { plandeB(); }
  });

})();
